import { IsAppInstalled, RoleDTO } from './server-types';
import {
  SimpleCondition,
  PolicyDTO,
  PredefinedAreaRole,
  PredefinedRole,
  Role,
  RolesData,
  Scope,
  Statement
} from '../types/roles';
import { i18nInstance } from '../i18n';
import { getPredefinedRoles } from './roleService';

export const convertRolesDTOToRolesData = (roles: RoleDTO[], isSiteLevel: boolean, isAppInstalled: IsAppInstalled, predefinedRoles?: PredefinedAreaRole[]): RolesData => {
  const rolesData = roles.map(role => convertRoleDTOToRoleData(role, isSiteLevel, isAppInstalled, predefinedRoles));
  const alreadySeenSet = new Set<string>();
  return rolesData.filter(_role => {
    if (_role && !alreadySeenSet.has(_role.id)) {
      alreadySeenSet.add(_role.id);
      return true;
    } else {
      return false;
    }
  });
};

export const convertRoleDTOToRoleData = (role: RoleDTO, isSiteLevel: boolean, isAppInstalled: IsAppInstalled, predefinedRoles?: PredefinedAreaRole[]): Role => {
  let key, restrictFromLevel;
  let { id, name: title, description, isCustom, scopes, experiments, deprecated } = role;
  if (isCustom) {
    key = id;
  } else {
    const predefinedRole = findPredefinedRole(id, isSiteLevel, predefinedRoles);

    //filters out roles that are not part of predefinedRolesConfig, restricted due to the level or installed apps or experiments
    if (shouldFilterOutRole(predefinedRole, isSiteLevel, isAppInstalled)) {
      return;
    }

    id = predefinedRole.id;
    title = i18nInstance.t(predefinedRole?.titleKey) || id;
    description = i18nInstance.t(predefinedRole?.descriptionKey);
    key = predefinedRole?.deprecatedKey;
    experiments = predefinedRole?.experiments;
    deprecated = predefinedRole?.deprecated;
    restrictFromLevel = predefinedRole?.restrictFromLevel;
  }
  return {
    id,
    key,
    title,
    description,
    isCustom,
    scopes,
    experiments,
    deprecated,
    restrictFromLevel,
  };
};

export const convertPolicyToRoleData = (policy: PolicyDTO): Role => {
  return {
    id: policy.id,
    key: policy.id,
    title: policy.name,
    description: policy.description,
    isCustom: true,
    scopes: policy.statements ? convertStatementsToScopes(policy.statements) : [],
  };
};

export const convertStatementsToScopes = (statements: Statement[]): string[] => {
  return statements.map(statement => statement.scopes).flat();
};

export const convertScopesToStatements = (scopes: Scope[], selectedScopes: string[], shouldSendCondition: boolean): Statement[] => {
  return getStatementsByLevel(scopes, selectedScopes, shouldSendCondition);
};

export const getStatementsByLevel = (scopes: Scope[], selectedScopes: string[], shouldSendCondition: boolean): Statement[] => {
  let scopesByLevel: { [key: string]: string[]} = selectedScopes.reduce((scopesByStatement, selectedScope) => {
    const scopeLevel = scopes.find(scope => scope.id === selectedScope)?.level ?? 'site';  //For Billing & Domains site dependencies
    if (!scopesByStatement[scopeLevel]) {
      scopesByStatement[scopeLevel] = [];
    }
    scopesByStatement[scopeLevel].push(selectedScope);
    return scopesByStatement;
  }, {});
  return Object.entries(scopesByLevel).map(([level, _scopes]) => {
    let intermediateStatement: Statement = {
      scopes: _scopes,
      effect: 'ALLOW',
    };
    const condition = getConditionByLevel(level, shouldSendCondition);
    if (condition) {
      Object.assign(intermediateStatement, {
        condition: {
          condition: {
            simpleCondition: condition
          }
        }
      });
    }
    return intermediateStatement;
  });
};

export const getConditionByLevel = (scopeLevel: string, shouldSendCondition: boolean): SimpleCondition | undefined =>
  (shouldSendCondition && scopeLevel === 'account') ?
    {
      conditionModelId: '2',
      attrName: 'msid',
      op: 'EQUAL',
      value: { attrName: '$ASSIGNMENT.RESOURCE.CTX_SITE' },
    } :
    undefined;

const findPredefinedRole = (roleId: string, isSiteLevel: boolean, predefinedRoles?: PredefinedAreaRole[]): PredefinedRole => {
  const predefinedRoleArr = getPredefinedRoles(predefinedRoles).filter(_predefinedRole => _predefinedRole.id === roleId || _predefinedRole.deprecatedKey === roleId);
  let predefinedRole;
  if (predefinedRoleArr.length > 1) {
    predefinedRole = predefinedRoleArr.find(_predefinedRole => isSiteLevel ? _predefinedRole.restrictFromLevel === 'account' : _predefinedRole.restrictFromLevel === 'site');
  }
  if (predefinedRoleArr.length === 1 || !predefinedRole) {
    predefinedRole = predefinedRoleArr[0];
  }
  return predefinedRole;
};

const shouldFilterOutRole = (predefinedRole: PredefinedRole, isSiteLevel: boolean, isAppInstalled: IsAppInstalled): boolean => {
  if (!predefinedRole) {
    return true;
  } else if (isSiteLevel && predefinedRole.appDefIds?.length > 0 && !predefinedRole.appDefIds.some(isAppInstalled)) {
    return true;
  }

  return false;
};
